/**
 * « Mes sinistres » (`/sinistres` — fiches 22736 / 22989): page header
 * (title, « {n} sinistres au total » — the VEOS total of the current search /
 * status —, breadcrumb Sinistres) and the configurable claims list
 * (`extranet.claimsListCard`) fed by `WS2_EA_TAB_SINISTRES`, re-fetched with
 * the list toolbar's `q` / `etat` (reported by the card through
 * `onFiltersChange`).
 *
 * The page exposes the extranet base rule context only (no claim-specific
 * variable on a list).
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { RuleContextProvider } from '@components/shared/configurable';

import type { ClaimListRow, ClaimsListFilters } from '../api/claimsService';
import { claimsCountSubtitle } from '../components/claims/claimsText';
import { ClaimsListCardConfigurable as ClaimsListCard } from '../components/claims/register';
import { queryCardState, useClaimsList } from '../components/claims/useClaimQueries';
import { PageHeader } from '../components/PageHeader';
import { useExtranetBaseRuleContext } from '../contextVariables';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

const NO_ROWS: readonly ClaimListRow[] = [];
const NO_FILTERS: ClaimsListFilters = {};

export function ClaimsPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const text = EXTRANET_PAGE_TEXT.claims;
  const ruleContext = useExtranetBaseRuleContext();
  // Search / status of the list card's toolbar (server-side, fiche 22989).
  const [filters, setFilters] = useState<ClaimsListFilters>(NO_FILTERS);
  const claims = useClaimsList(filters);

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-claims">
        <PageHeader
          title={text.title}
          subtitle={claims.data ? claimsCountSubtitle(claims.data.total) : undefined}
          backTo={extranetPath(tenantSlug)}
          breadcrumbs={[{ key: 'sinistres', label: text.breadcrumb }]}
        />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <ClaimsListCard
            rows={claims.data?.rows ?? NO_ROWS}
            {...queryCardState(claims)}
            onFiltersChange={setFilters}
          />
        </div>
      </div>
    </RuleContextProvider>
  );
}
